import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, AlertTriangle } from "lucide-react";
import {
  useTasks,
  useUpdateTask,
  TASK_STATUS_LABEL,
  TASK_PRIORITY_LABEL,
  TaskStatus,
  TaskPriority,
  taskClientName,
  isOverdue,
} from "@/api/tasks";
import { useAuthStore } from "@/stores/authStore";
import toast from "react-hot-toast";
import { getApiErrorMessage } from "@/api/client";
import CreateTaskModal from "./CreateTaskModal";

const STATUS_STYLE: Record<TaskStatus, string> = {
  PENDIENTE: "bg-gray-100 text-gray-600",
  EN_PROCESO: "bg-blue-100 text-blue-700",
  EN_REVISION: "bg-purple-100 text-purple-700",
  BLOQUEADA: "bg-red-100 text-red-700",
  APROBADA: "bg-teal-100 text-teal-700",
  FINALIZADA: "bg-green-100 text-green-700",
  CANCELADA: "bg-gray-100 text-gray-400",
};

const PRIORITY_STYLE: Record<TaskPriority, string> = {
  BAJA: "bg-gray-100 text-gray-500",
  MEDIA: "bg-blue-100 text-blue-600",
  ALTA: "bg-amber-100 text-amber-700",
  URGENTE: "bg-red-100 text-red-700",
};

const STATUS_OPTIONS = Object.keys(TASK_STATUS_LABEL) as TaskStatus[];
const PRIORITY_OPTIONS = Object.keys(TASK_PRIORITY_LABEL) as TaskPriority[];

export default function TasksPage() {
  const [status, setStatus] = useState<TaskStatus | "">("");
  const [priority, setPriority] = useState<TaskPriority | "">("");
  const [search, setSearch] = useState("");
  const [overdue, setOverdue] = useState(false);
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useTasks({
    status: status || undefined,
    priority: priority || undefined,
    search: search || undefined,
    overdue: overdue || undefined,
    page,
    pageSize: 15,
  });
  const updateTask = useUpdateTask();

  const tasks = data?.data ?? [];
  const meta = data?.meta;

  const handleStatusChange = async (id: string, newStatus: TaskStatus) => {
    try {
      await updateTask.mutateAsync({ id, status: newStatus });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Tareas</h1>
          <p className="text-sm text-gray-500">Bandeja de trabajo de la firma: tareas por cliente y tareas internas.</p>
        </div>
        {hasPermission("tasks.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nueva tarea
          </button>
        )}
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input
          type="search"
          placeholder="Buscar por título..."
          className="input-field min-w-[220px] flex-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input-field w-auto"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as TaskStatus | "");
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {TASK_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          className="input-field w-auto"
          value={priority}
          onChange={(e) => {
            setPriority(e.target.value as TaskPriority | "");
            setPage(1);
          }}
        >
          <option value="">Todas las prioridades</option>
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {TASK_PRIORITY_LABEL[p]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={overdue}
            onChange={(e) => {
              setOverdue(e.target.checked);
              setPage(1);
            }}
          />
          Solo vencidas
        </label>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Tarea</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Vence</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Prioridad</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Responsable</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && tasks.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  No hay tareas que coincidan con los filtros.
                </td>
              </tr>
            )}
            {tasks.map((task) => (
              <tr key={task.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{task.title}</p>
                  {task.tags.length > 0 && <p className="text-xs text-gray-400">{task.tags.join(", ")}</p>}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {task.client ? (
                    <Link to={`/clients/${task.client.id}`} className="hover:text-brand-600">
                      {taskClientName(task)}
                    </Link>
                  ) : (
                    <span className="text-gray-400">Interna</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {task.dueDate ? (
                    <span className={isOverdue(task) ? "inline-flex items-center gap-1 text-red-600" : "text-gray-600"}>
                      {isOverdue(task) && <AlertTriangle size={12} />}
                      {task.dueDate}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_STYLE[task.priority]}`}>
                    {TASK_PRIORITY_LABEL[task.priority]}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{task.assignedTo?.fullName ?? "Sin asignar"}</td>
                <td className="px-4 py-3">
                  {hasPermission("tasks.update") ? (
                    <select
                      className={`rounded-full border-0 px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[task.status]}`}
                      value={task.status}
                      onChange={(e) => handleStatusChange(task.id, e.target.value as TaskStatus)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {TASK_STATUS_LABEL[s]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[task.status]}`}>
                      {TASK_STATUS_LABEL[task.status]}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} tareas)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button
                className="btn-secondary"
                disabled={!!meta.totalPages && page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <CreateTaskModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
